import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setManageShareListModalIsOpen from "@insite/client-framework/Store/Components/ManageShareListModal/Handlers/SetManageShareListModalIsOpen";
import setShareListDetailModalIsOpen from "@insite/client-framework/Store/Components/ShareListDetailModal/Handlers/SetShareListDetailModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import Lock from "@insite/mobius/Icons/Lock";
import Users from "@insite/mobius/Icons/Users";
import Link from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: WishListSharingStatusStyles;
    /**
     * @deprecated Use the `wishList` property instead.
     */
    isSharedList?: boolean;
    /**
     * @deprecated Use the `wishList` property instead.
     */
    wishListSharesCount?: number;
    /**
     * @deprecated Use the `wishList` property instead.
     */
    sharedByDisplayName?: string;
    wishList?: WishListModel;
    showNoPermissionsTooltip?: boolean;
}

const mapDispatchToProps = {
    setManageShareListModalIsOpen,
    setShareListDetailModalIsOpen,
    loadWishList,
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    wishListState: ownProps.wishList ? getWishListState(state, ownProps.wishList.id) : undefined,
});

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface WishListSharingStatusStyles {
    statusText?: TypographyProps;
    privateIcon?: IconPresentationProps;
    sharedIcon?: IconPresentationProps;
    privateTooltip?: TooltipPresentationProps;
    autogeneratedIcon?: IconPresentationProps;
    autogeneratedTooltip?: TooltipPresentationProps;
}

export const wishListSharingStatusStyles: WishListSharingStatusStyles = {
    privateTooltip: {
        cssOverrides: {
            tooltipContainer: css`
                width: 13.75rem;
                position: absolute;
                display: inline-flex;
                justify-content: flex-end;
                bottom: -3.25rem;
                left: 2.5rem;

                &::after {
                    transform: rotate(90deg);
                    content: "";
                    position: absolute;
                    top: calc(50% - 1rem);
                    left: -14%;
                    margin-left:0;
                    border-width: 1rem;
                    border-style: solid;
                    border-color: #e5f5fc transparent transparent transparent;
                }
            `,
            tooltipBody: css`
                background: #e5f5fc;
            `,
        },
    },
    privateIcon: {
        src: HelpCircle,
        size: 15,
        css: css`
            margin-left: 5px;
        `,
    },
    sharedIcon: {
        src: Users,
        size: 15,
        css: css`
            margin-right: 5px;
        `,
    },
    autogeneratedIcon: {
        src: Lock,
        size: 15,
        css: css`
            margin-right: 5px;
        `,
    },
};

const WishListSharingStatus: FC<Props> = ({
    extendedStyles,
    isSharedList,
    wishListSharesCount,
    sharedByDisplayName,
    wishList,
    setManageShareListModalIsOpen,
    setShareListDetailModalIsOpen,
    wishListSettings,
    showNoPermissionsTooltip,
    wishListState,
    loadWishList,
}) => {
    const [styles] = React.useState(() => mergeToNew(wishListSharingStatusStyles, extendedStyles));

    useEffect(() => {
        if (!wishList?.id || wishListState?.isLoading || wishListState?.value?.sharedUsers != null) {
            return;
        }
        loadWishList({
            wishListId: wishList.id,
            exclude: ["listLines"],
            expand: ["sharedUsers", "favorite", "hidden", "tags"],
        });
    }, [wishList?.id, wishListState?.value?.sharedUsers]);

    if (!wishList || !wishListSettings?.allowMultipleWishLists || !wishListSettings?.allowListSharing) {
        return null;
    }

    const manageShareListClickHandler = () => {
        setManageShareListModalIsOpen({ modalIsOpen: true, wishListId: wishList.id });
    };

    const shareListDetailClickHandler = () => {
        setShareListDetailModalIsOpen({ modalIsOpen: true, listOwner: wishList?.sharedByDisplayName });
    };

    const isSharedListInner = isSharedList || wishList.isSharedList || false;
    const sharedUsers = wishListState?.value?.sharedUsers;
    const wishListSharesCountInner = sharedUsers != null
        ? [...new Set(sharedUsers.map(item => item.displayName))].length
        : (wishListSharesCount || wishList.wishListSharesCount || 0);
    const sharedByDisplayNameInner = sharedByDisplayName || wishList.sharedByDisplayName;

    if (wishList.isAutogenerated) {
        return (
            <Typography {...styles.statusText} data-test-selector="myLists_autogenerated">
                <Icon {...styles.autogeneratedIcon} />
                {translate("Auto-generated")}
                <Tooltip {...styles.privateTooltip} text={siteMessage("Lists_Autogenerated_List_Tooltip") as string} />
            </Typography>
        );
    }

    return (
        <>
            {!isSharedListInner && wishList.shareOption === "Private" && (
                <Typography {...styles.statusText} data-test-selector="myLists_PrivateLabel">
                    {translate("Private")}
                    <Tooltip {...styles.privateTooltip} text={siteMessage("Lists_Share_List_Tooltip") as string} />
                </Typography>
            )}
            {!isSharedListInner && wishList.shareOption !== "Private" && (
                <>
                    <Link
                        {...styles.statusText}
                        onClick={() => manageShareListClickHandler()}
                        data-test-selector="shareWithOthers"
                    >
                        <Icon {...styles.sharedIcon} />
                        {translate("Shared with")} {wishListSharesCountInner} {translate("others")}
                    </Link>
                </>
            )}
            {isSharedListInner && sharedByDisplayNameInner && (
                <Typography {...styles.statusText} data-test-selector="myLists_listSharedBy">
                    <Link
                        {...styles.statusText}
                        onClick={() => shareListDetailClickHandler()}
                        data-test-selector="sharedBy"
                    >
                        <Icon {...styles.sharedIcon} />
                        {translate("Shared by")} {sharedByDisplayNameInner}
                    </Link>
                    {!wishList?.allowEdit && showNoPermissionsTooltip && (
                        <Tooltip
                            {...styles.privateTooltip}
                            text={siteMessage("Lists_Share_List_No_Permissions_Tooltip") as string}
                        />
                    )}
                </Typography>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WishListSharingStatus);
